/**
 * DealSubmissionsTab
 *
 * Renders the Submissions tab on the Deal Detail page.
 * - Table of credit applications for the deal
 * - Status badges with colour coding (8 states)
 * - Create/Edit via modal, Delete via AlertDialog
 * - Fetches lender names via API JOIN (returned by GET /api/applications)
 *
 * AC001: Submissions tab added to deal detail
 * AC002: Applications table displays all applications for deal
 * AC003: Create application modal
 * AC004: Edit application modal
 * AC005: Delete with confirmation
 * AC006: Status badge colours
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Eye, Edit, Trash2, Plus, Loader2, List, LayoutGrid } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { CreateApplicationModal } from "@/components/modals/create-application-modal";
import { ApplicationDetailPanel } from "@/components/deals/application-detail-panel";
import { LenderComparisonPanel } from "@/components/deals/lender-comparison-panel";

// Status badge colours (AC006)
const APPLICATION_STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  pending_review: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200",
  in_review: "bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200",
  approved: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200",
  conditionally_approved: "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200",
  declined: "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200",
  withdrawn: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
  expired: "bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-200",
};

const APPLICATION_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  pending_review: "Pending Review",
  in_review: "In Review",
  approved: "Approved",
  conditionally_approved: "Conditionally Approved",
  declined: "Declined",
  withdrawn: "Withdrawn",
  expired: "Expired",
};

const APPLICATION_TYPE_LABELS: Record<string, string> = {
  new: "New",
  variation: "Variation",
  refinance: "Refinance",
  top_up: "Top Up",
  restructure: "Restructure",
};

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

interface ApplicationRow {
  id: string;
  dealId: string;
  applicationReference: string | null;
  lenderReference: string | null;
  lenderId: string | null;
  lenderName: string | null;
  lenderProductId: string | null;
  applicationType: string;
  applicationStatus: string;
  requestedAmount: string | null;
  approvedAmount: string | null;
  facilityLimit: string | null;
  proposedRate: string | null;
  approvedRate: string | null;
  assessmentRate: string | null;
  createdDate: string | null;
  submittedDate: string | null;
  decisionDate: string | null;
  approvalExpiryDate: string | null;
  expectedSettlementDate: string | null;
  actualSettlementDate: string | null;
  servicingAtActualRate: string | null;
  servicingAtAssessmentRate: string | null;
  lvrTotal: string | null;
  declineReason: string | null;
  withdrawalReason: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DealSubmissionsTabProps {
  dealId: string;
}

export function DealSubmissionsTab({ dealId }: DealSubmissionsTabProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<ApplicationRow | null>(null);
  const [viewingApp, setViewingApp] = useState<ApplicationRow | null>(null);
  const [deleteApp, setDeleteApp] = useState<ApplicationRow | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "compare">("list");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // AC002: Fetch applications for deal — uses default getQueryFn with auth
  const { data: applications = [], isLoading } = useQuery<ApplicationRow[]>({
    queryKey: ["/api/applications", { dealId }],
    enabled: !!dealId,
  });

  // AC005: Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/applications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/applications"] });
      toast({ title: "Application deleted" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete application",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (app: ApplicationRow) => {
    setEditingApp(app);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingApp(null);
    setIsModalOpen(true);
  };

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setEditingApp(null);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading applications...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Credit Applications</h3>
            <div className="flex items-center gap-2">
              {applications.length >= 2 && (
                <div className="flex items-center border rounded-md" data-testid="view-toggle">
                  <Button
                    variant={viewMode === "list" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-8 rounded-r-none"
                    onClick={() => setViewMode("list")}
                    data-testid="button-view-list"
                  >
                    <List className="mr-1 h-3.5 w-3.5" />
                    List
                  </Button>
                  <Button
                    variant={viewMode === "compare" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-8 rounded-l-none"
                    onClick={() => setViewMode("compare")}
                    data-testid="button-view-compare"
                  >
                    <LayoutGrid className="mr-1 h-3.5 w-3.5" />
                    Compare
                  </Button>
                </div>
              )}
              <Button size="sm" onClick={handleCreate} data-testid="button-create-application">
                <Plus className="mr-1 h-4 w-4" />
                Create Application
              </Button>
            </div>
          </div>

          {viewMode === "list" ? (
            <>
              {applications.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="empty-state-applications">
                  <p>No applications yet. Create your first application to begin the submission process.</p>
                  <Button onClick={handleCreate} className="mt-4">
                    Create Application
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lender</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Decision</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {applications.map((app) => (
                      <TableRow key={app.id} data-testid={`application-row-${app.id}`}>
                        <TableCell className="font-medium">
                          {app.lenderName || "No lender"}
                        </TableCell>
                        <TableCell>
                          {APPLICATION_TYPE_LABELS[app.applicationType] || app.applicationType}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={`text-xs ${APPLICATION_STATUS_COLORS[app.applicationStatus] || "bg-gray-100 text-gray-800"}`}
                            data-testid={`status-badge-${app.applicationStatus}`}
                          >
                            {APPLICATION_STATUS_LABELS[app.applicationStatus] || app.applicationStatus}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(app.requestedAmount)}
                        </TableCell>
                        <TableCell>{formatDate(app.submittedDate)}</TableCell>
                        <TableCell>{formatDate(app.decisionDate)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setViewingApp(app)}
                              data-testid={`button-view-${app.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleEdit(app)}
                              data-testid={`button-edit-${app.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => setDeleteApp(app)}
                              data-testid={`button-delete-${app.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          ) : (
            <LenderComparisonPanel applications={applications} />
          )}
        </CardContent>
      </Card>

      {/* F006: Application Detail Panel (replaces simple AlertDialog) */}
      {viewingApp && (
        <ApplicationDetailPanel
          applicationId={viewingApp.id}
          dealId={dealId}
          application={viewingApp}
          onClose={() => setViewingApp(null)}
        />
      )}

      {/* AC005: Delete Confirmation */}
      <AlertDialog open={!!deleteApp} onOpenChange={(open) => !open && setDeleteApp(null)}>
        <AlertDialogContent data-testid="dialog-delete-application">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this credit application. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-application">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteApp) {
                  deleteMutation.mutate(deleteApp.id);
                  setDeleteApp(null);
                }
              }}
              data-testid="button-confirm-delete-application"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AC003/AC004: Create/Edit Modal */}
      <CreateApplicationModal
        open={isModalOpen}
        onOpenChange={handleModalClose}
        dealId={dealId}
        editingApplication={editingApp}
      />
    </>
  );
}
